/** Espelho dos contratos do certidoes-api. Um lugar só para mudar quando a API mudar. */

export type RequestStatus =
  | 'DRAFT'
  | 'SUBMITTED'
  | 'IN_PROGRESS'
  | 'AWAITING_CITIZEN'
  | 'COMPLETED'
  | 'REJECTED'
  | 'CANCELLED';

export interface Profile {
  cpf: string;
  formattedCpf: string;
  name: string | null;
  socialName: string | null;
  motherName: string | null;
  birthDate: string | null;
  email: string | null;
  emailConfirmed: boolean;
  phone: string | null;
  phoneConfirmed: boolean;
  missingFields: string[];
  profileComplete: boolean;
  readyToSubmit: boolean;
}

export interface IncidentLocation {
  postalCode?: string | null;
  state?: string | null;
  city?: string | null;
  district?: string | null;
  street?: string | null;
  number?: string | null;
  complement?: string | null;
}

export interface CertificateRequestSummary {
  id: string;
  protocol: string | null;
  agency: string;
  status: RequestStatus;
  incidentType: string | null;
  incidentDate: string | null;
  incidentTime: string | null;
  locationSummary: string | null;
  createdAt: string;
  updatedAt: string;
  submittedAt: string | null;
  expiresAt: string | null;
}

export interface TimelineEvent {
  action: string;
  previousStatus: RequestStatus | null;
  newStatus: RequestStatus | null;
  details: string | null;
  occurredAt: string;
}

export interface CertificateRequest extends CertificateRequestSummary {
  description: string | null;
  location: IncidentLocation;
  statusReason: string | null;
  pendingIssues: string[];
  timeline?: TimelineEvent[];
  /**
   * HTML da certidão, já sanitizado no servidor. Só vem preenchido quando
   * status é COMPLETED — a API nunca manda um rascunho em elaboração pra cá.
   * Ainda assim é renderizado com DOMPurify no cliente antes de qualquer
   * dangerouslySetInnerHTML (defesa em profundidade).
   */
  certificateContent: string | null;
}

export interface Page<T> {
  items: T[];
  total: number;
  page: number;
  perPage: number;
}

export interface Option {
  value: string;
  label: string;
}

export interface ReferenceData {
  agencies: Option[];
  incidentTypes: Option[];
}

export interface VerificationCodeRequestResult {
  recipient: string;
  expiresAt: string;
}

/** Rótulos em português — o enum é técnico, isto é o que o cidadão lê. */
export const STATUS_LABELS: Record<RequestStatus, string> = {
  DRAFT: 'Rascunho',
  SUBMITTED: 'Enviada',
  // Mesma palavra usada na linha do tempo e no e-mail — "Em atendimento"
  // e "Em andamento" lado a lado confundiam o cidadão.
  IN_PROGRESS: 'Em andamento',
  AWAITING_CITIZEN: 'Aguardando você',
  // Reaproveita COMPLETED como "Emitida" — mesma convenção do backoffice.
  COMPLETED: 'Emitida',
  REJECTED: 'Indeferida',
  CANCELLED: 'Cancelada',
};

/** Severidades do Tag do @uigovpe (PrimeReact). */
export const STATUS_SEVERITIES: Record<
  RequestStatus,
  'info' | 'success' | 'warning' | 'danger' | undefined
> = {
  DRAFT: undefined,
  SUBMITTED: 'info',
  IN_PROGRESS: 'info',
  AWAITING_CITIZEN: 'warning',
  COMPLETED: 'success',
  REJECTED: 'danger',
  CANCELLED: undefined,
};

export const EVENT_ACTION_LABELS: Record<string, string> = {
  DRAFT_OPENED: 'Solicitação iniciada',
  DRAFT_UPDATED: 'Rascunho atualizado',
  DRAFT_DISCARDED: 'Rascunho descartado',
  REQUEST_SUBMITTED: 'Solicitação enviada',
  REQUEST_VIEWED: 'Analisada pela equipe',
  STATUS_CHANGED: 'Situação atualizada',
  CERTIFICATE_EMITTED: 'Certidão emitida',
};
